import { defineStore } from "pinia";
import keycloakClient from '@/clients/keycloak.client';
import sonnnigApiClient from "@/clients/sonnnig-api.client";
import type {KeycloakInfo, User} from '@/models/stored/user';
import {ref} from "vue";

export type AuthStore = ReturnType<typeof useAuthStore>

export const useAuthStore = defineStore("storeAuth", () => {
        const user = ref({} as User);
        async function initStoreValues(authInfo: KeycloakInfo, clearData = true) {
            console.log("auth: initializing store values");
            //console.log(authInfo);

            if(clearData) {
                clearUserData();
            }
            user.value.authInfo = authInfo;

            await sonnnigApiClient.initialize();

            if(user.value.authInfo.authenticated){
                await identity();
            }

            console.log(user.value);
        }

        // Logout user
        async function logout() {
            console.log("auth: logout");

            try {
                keycloakClient.logout(import.meta.env.VITE_APP_BASE_URI + "/login");
                clearUserData();
            } catch (error) {
                console.error(error);
            }
        }

        async function login(url: string){
            console.log("auth: login");
            try {
                await keycloakClient.login(import.meta.env.VITE_APP_BASE_URI + url);
            } catch (error) {
                console.error(error);
            }
        }

        // Refresh user's token
        async function refreshUserToken() {
            console.log("auth: refresh token")

            try {
                const authInfo = await keycloakClient.refreshToken();

                if(authInfo == undefined){

                    return;
                }

                await initStoreValues(authInfo, false);
            } catch (error) {
                console.error(error);
            }
        }
        // Clear user's store data
        function clearUserData() {
            user.value = {} as User;
        }

        function authenticated(){
            return user.value.authInfo.authenticated;
        }

        function getRoles(): string[] {
            if (!user.value.authInfo?.token) return [];

            try {
                // Decode the JWT token
                const tokenParts = user.value.authInfo.token.split('.');
                if (tokenParts.length !== 3) return [];

                const payload = JSON.parse(atob(tokenParts[1]));

                // Check for the roles in realm_access.roles
                const roles = payload.realm_access?.roles || [];
                return roles;
            } catch (error) {
                console.error('Error decoding token:', error);
                return [];
            }
        }

        function isSuperAdmin(): boolean {
            return getRoles().includes("sonnnig_super_admin");
        }

        function isAdmin(): boolean {
            return getRoles().includes("sonnnig_admin");
        }

        function isDeveloper(): boolean {
            return getRoles().includes("developer");
        }

        async function identity(force: boolean = false){
            const ONE_HOUR = 60 * 60 * 1000;
            if(force || user.value.lastIdentity == undefined || ((new Date()).getDate() - user.value.lastIdentity.getDate()) > ONE_HOUR){
                let identityDto = await sonnnigApiClient.identity();
                user.value.onboardingStatus = identityDto.onboardingStatus;
                user.value.firstName = identityDto.firstName;
                user.value.secondName = identityDto.secondName;
                user.value.lastIdentity = new Date();
            }
        }

        return {
            user,
            authenticated,
            isSuperAdmin,
            isAdmin,
            isDeveloper,
            refreshUserToken,
            login,
            logout,
            initStoreValues,
            identity,
            getRoles
        };
});

export default useAuthStore;